import type {
  CanvasCapture,
  CanvasProject,
  CanvasSession,
  CaptureUploadTarget,
  FixListEntry,
  FixListStatus,
  Issue,
  StartSessionResult,
} from '@repo/contracts';

import { logger } from '../lib/logger';
import { ApiError, apiFetch } from './client';

const log = logger.child({ module: 'canvas-captures' });

/**
 * The board's contents, as server state.
 *
 * Captures used to live in the browser-session hook, which meant they vanished
 * on reload and the arrangement was lost with them. They are rows the API owns
 * now, so TanStack Query owns them here (CLAUDE.md > Frontend state) and the
 * session hook is back to owning only the live socket.
 */

export const canvasKeys = {
  all: ['canvas'] as const,
  project: (projectId: string) =>
    [...canvasKeys.all, 'project', projectId] as const,
  projects: () => [...canvasKeys.all, 'projects'] as const,
  /** Every room the account has, across the hidden per-site projects. */
  rooms: () => [...canvasKeys.all, 'rooms'] as const,
  session: (sessionId: string) =>
    [...canvasKeys.all, 'session', sessionId] as const,
  sessions: (projectId: string) =>
    [...canvasKeys.all, 'sessions', projectId] as const,
  projectCaptures: (projectId: string) =>
    [...canvasKeys.all, 'project-captures', projectId] as const,
  sessionCaptures: (sessionId: string) =>
    [...canvasKeys.all, 'session-captures', sessionId] as const,
};

export interface CreateCapturePayload {
  sessionId: string;
  pageUrl: string;
  device: 'desktop' | 'tablet' | 'mobile';
  /** The worker's DOM snapshot, stored beside the PNG. */
  dom?: Record<string, unknown> | null;
  /** The path the upload target was issued for, not a client-chosen one. */
  storagePath: string;
  width: number;
  height: number;
  dpr: number;
  fullPage: boolean;
}

/** base64 → Blob, without a data: URL round trip through fetch(). */
function pngBlob(base64: string): Blob {
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) bytes[i] = binary.charCodeAt(i);
  return new Blob([bytes], { type: 'image/png' });
}

/**
 * Puts the PNG in the bucket and returns the path it landed at.
 *
 * The bytes go browser → Cloud Storage directly, never through the API: a 2x
 * viewport PNG is megabytes, and passing them through Cloud Run costs CPU,
 * request time and a raised body limit for nothing. The API's only job is to
 * say where they may go (it picks the path) and to record the row afterwards.
 *
 * Deliberately not apiFetch: this is a signed URL on a different origin, and
 * attaching our session bearer to it would both fail the signature and leak
 * the token to Google.
 */
export async function uploadCaptureImage(
  /** base64 PNG as the worker sent it, or a Blob the tab made itself (a
   *  stitched page) — which goes up as-is, with no base64 round trip. */
  image: Blob | string,
): Promise<string> {
  const target = await apiFetch<CaptureUploadTarget>(
    '/canvas/captures/upload-url',
    { method: 'POST' },
  );

  const response = await fetch(target.uploadUrl, {
    body: typeof image === 'string' ? pngBlob(image) : image,
    // Must match the Content-Type the URL was signed for exactly, or GCS
    // rejects the PUT with a 403 that names nothing.
    headers: { 'Content-Type': target.contentType },
    method: 'PUT',
  });

  if (!response.ok) {
    log.error(
      { status: response.status, storagePath: target.storagePath },
      'capture upload to storage failed',
    );
    throw new ApiError('Could not upload the capture', response.status);
  }

  return target.storagePath;
}

export function listProjects(): Promise<CanvasProject[]> {
  return apiFetch<CanvasProject[]>('/canvas/projects');
}

export function getProject(projectId: string): Promise<CanvasProject> {
  return apiFetch<CanvasProject>(`/canvas/projects/${projectId}`);
}

/** An empty, named project — sessions are added to it afterwards. */
export function createProject(name: string): Promise<CanvasProject> {
  return apiFetch<CanvasProject>('/canvas/projects', {
    body: JSON.stringify({ name }),
    method: 'POST',
  });
}

/** Renames a project, or sets its GitHub repo/branch — the only fields this
 *  route accepts (`UpdateCanvasProjectDto`). */
export function updateProject(
  projectId: string,
  patch: { githubBranch?: string; githubRepo?: string; name?: string },
): Promise<CanvasProject> {
  return apiFetch<CanvasProject>(`/canvas/projects/${projectId}`, {
    body: JSON.stringify(patch),
    method: 'PATCH',
  });
}

export function listSessions(projectId: string): Promise<CanvasSession[]> {
  return apiFetch<CanvasSession[]>(`/canvas/projects/${projectId}/sessions`);
}

/** Every room, newest first — what home lists. */
export function listRooms(): Promise<CanvasSession[]> {
  return apiFetch<CanvasSession[]>('/canvas/sessions');
}

export function getSession(sessionId: string): Promise<CanvasSession> {
  return apiFetch<CanvasSession>(`/canvas/sessions/${sessionId}`);
}

/**
 * Starts a session — a room — against a URL. Without a `projectId` the
 * server puts it in the hidden project for the URL's site, which is how it
 * starts with the panel earlier rooms on that site already have.
 */
export function startSession(payload: {
  projectId?: string;
  url: string;
}): Promise<StartSessionResult> {
  return apiFetch<StartSessionResult>('/canvas/sessions', {
    body: JSON.stringify(payload),
    method: 'POST',
  });
}

/** One board. */
export function listSessionCaptures(
  sessionId: string,
): Promise<CanvasCapture[]> {
  return apiFetch<CanvasCapture[]>(
    `/canvas/captures?sessionId=${encodeURIComponent(sessionId)}`,
  );
}

/** Every capture of a project, across sessions — the dashboard's preview reel. */
export function listProjectCaptures(
  projectId: string,
): Promise<CanvasCapture[]> {
  return apiFetch<CanvasCapture[]>(
    `/canvas/captures?projectId=${encodeURIComponent(projectId)}`,
  );
}

export function createCapture(
  payload: CreateCapturePayload,
): Promise<CanvasCapture> {
  return apiFetch<CanvasCapture>('/canvas/captures', {
    body: JSON.stringify(payload),
    method: 'POST',
  });
}

export function moveCapture(
  id: string,
  x: number,
  y: number,
): Promise<CanvasCapture> {
  return apiFetch<CanvasCapture>(`/canvas/captures/${id}`, {
    body: JSON.stringify({ x, y }),
    method: 'PATCH',
  });
}

export function addToFixList(payload: {
  projectId: string;
  /** Absent for a project-level finding — a manually reported issue has no
   *  capture or session of its own; the DTO itself has always had it optional. */
  sessionId?: string;
  issue: Issue;
  status: FixListStatus;
}): Promise<FixListEntry> {
  return apiFetch<FixListEntry>('/fix-list', {
    body: JSON.stringify(payload),
    method: 'POST',
  });
}

/** Starts the coding agent on one entry — an explicit choice from the fix
 *  list, never fired just from reporting or triaging an issue. */
export function dispatchFixAgent(id: string): Promise<FixListEntry> {
  return apiFetch<FixListEntry>(`/fix-list/${id}/dispatch`, {
    method: 'POST',
  });
}

/** A triage decision on one fix-list entry: fixed, won't fix, not an issue,
 *  or back to fix. */
export function setFixStatus(
  id: string,
  status: FixListStatus,
): Promise<FixListEntry> {
  return apiFetch<FixListEntry>(`/fix-list/${encodeURIComponent(id)}`, {
    body: JSON.stringify({ status }),
    method: 'PATCH',
  });
}

export function deleteCapture(id: string): Promise<{ success: true }> {
  return apiFetch<{ success: true }>(`/canvas/captures/${id}`, {
    method: 'DELETE',
  });
}
